import { booleanWithin, booleanIntersects, polygonToLine } from '@turf/turf';
import Polygon from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';
import Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import GeometryType from '../constants/geometryType';

const isArrayOfMinLength = (arr, minLength) => Array.isArray(arr) && arr.length >= minLength;

/**
 * Checks if a coordinate is valid (an array of two finite numbers).
 * @param {Array<number>} coord
 * @returns {boolean}
 */
const isValidCoordinate = (coord) => (
    Array.isArray(coord) &&
    coord.length >= 2 &&
    Number.isFinite(coord[0]) &&
    Number.isFinite(coord[1])
);

/**
 * Closes a ring of coordinates if it is not already closed.
 * A ring is considered closed if the first and last coordinates are the same.
 *
 * @param {Array<Array<number>>} ring - An array of coordinates representing a ring.
 * @returns {Array<Array<number>>|null} - The closed ring or null if invalid.
 */
const closeRingIfNeeded = (ring) => {
    if (!isArrayOfMinLength(ring, 3)) {
        return null;
    }

    const cleaned = ring.filter(isValidCoordinate);
    if (cleaned.length < 3) {
        return null;
    }

    const first = cleaned[0];
    const last = cleaned[cleaned.length - 1];

    const [x, y] = first;
    const [lx, ly] = last;
    const isClosed = x === lx && y === ly;

    const closed = isClosed ? cleaned : [...cleaned, [...first]];
    return closed;
};

/**
 * Sanitises a polygon's coordinates by ensuring each ring is valid and closed.
 * @param {Array<Array<number>>} polygonCoords
 * @returns {object|null} - A sanitised geometry or null if invalid.
 */
const sanitizePolygonCoordinates = (polygonCoords) => {
    if (!isArrayOfMinLength(polygonCoords, 1)) {
        return null;
    }

    const rings = polygonCoords
        .map(closeRingIfNeeded)
        .filter(Boolean);

    return rings.length > 0 ? rings : null;
};

export const sanitizeTurfGeometry = (geometry) => {
    const type = geometry?.type || geometry?.getType?.();
    const coordinates = geometry?.coordinates || geometry?.getCoordinates?.();
    if (!type || !Array.isArray(coordinates)) {
        return null;
    }

    if (type === GeometryType.POLYGON) {
        const polygon = sanitizePolygonCoordinates(coordinates);
        return polygon ? { type: GeometryType.POLYGON, coordinates: polygon } : null;
    }

    if (type === GeometryType.MULTI_POLYGON) {
        const multipolygon = coordinates
            .map(sanitizePolygonCoordinates)
            .filter(Boolean);

        if (multipolygon.length === 0) {
            return null;
        }

        if (multipolygon.length === 1) {
            return { type: GeometryType.POLYGON, coordinates: multipolygon[0] };
        }

        return { type: GeometryType.MULTI_POLYGON, coordinates: multipolygon };
    }

    return null;
};

/**
 * Merges an array of service area objects (from the myCoverage API response)
 * into a single OL MultiPolygon geometry. Each service area has a `geometry`
 * property with type "Polygon" or "MultiPolygon" and a `coordinates` array.
 *
 * @param {Array} serviceAreas - Array of service area objects with `.geometry.coordinates` and `.geometry.type`.
 * @returns {import('ol/geom/MultiPolygon').default|null} Merged OL MultiPolygon, or null if no valid areas.
 */
export const buildServiceAreaGeometry = (serviceAreas) => {
    if (!isArrayOfMinLength(serviceAreas, 1)) {
        return null;
    }

    const allPolygonCoords = [];

    serviceAreas.forEach(area => {
        const geom = area?.geometry;
        if (!geom?.coordinates) {
            return;
        }

        if (geom.type === GeometryType.MULTI_POLYGON) {
            geom.coordinates.forEach(polygonCoords => {
                allPolygonCoords.push(polygonCoords);
            });
        } else if (geom.type === GeometryType.POLYGON) {
            allPolygonCoords.push(geom.coordinates);
        }
    });

    if (allPolygonCoords.length === 0) {
        return null;
    }

    return new MultiPolygon(allPolygonCoords);
};

/**
 * Builds the two features that ServiceAreaOverlay uses to grey out the map
 * everywhere except the user's service area.
 *
 * 1. `worldFeature` is a large rectangle that covers the entire map — it gets
 *     filled with grey to indicate "you cannot draw here".
 * 2. `serviceAreaFeature` is the user's service area — it gets rendered on top
 *     with a Canvas composite operation that erases the grey, leaving the
 *     drawable area clear.
 *
 * @param {import('ol/geom/MultiPolygon').default} serviceAreaGeometry - Merged service area geometry.
 * @param {string} epsgCode - The EPSG code (e.g. '27700' or '29903').
 * @returns {{ worldFeature: import('ol/Feature').default, serviceAreaFeature: import('ol/Feature').default }|null}
 */
export const buildServiceAreaMask = (serviceAreaGeometry, epsgCode) => {
    if (!serviceAreaGeometry) {
        return null;
    }

    const worldExtents = {
        27700: [[-1000000, -1000000], [1500000, -1000000], [1500000, 1500000], [-1000000, 1500000], [-1000000, -1000000]],
        29903: [[-1000000, -1000000], [1500000, -1000000], [1500000, 1500000], [-1000000, 1500000], [-1000000, -1000000]]
    };

    const worldRing = worldExtents[epsgCode] || worldExtents['27700'];

    return {
        worldFeature: new Feature({ geometry: new Polygon([worldRing]) }),
        serviceAreaFeature: new Feature({ geometry: serviceAreaGeometry })
    };
};

/**
 * Converts an OL geometry (from a draw/modify feature) to a Turf GeoJSON geometry
 * for spatial operations.
 *
 * @param {import('ol/geom/Geometry').default} olGeometry - The OL geometry to convert.
 * @returns {object|null} A Turf-compatible GeoJSON geometry, or null.
 */
export const olGeometryToTurf = (olGeometry) => {
    if (!olGeometry) {
        return null;
    }

    try {
        const format = new GeoJSON();
        const geoJson = JSON.parse(format.writeGeometry(olGeometry));
        return geoJson;
    } catch (e) {
        return null;
    }
};

/**
 * Checks whether a drawn/edited geometry is fully contained within the service area.
 *
 * Turf.js booleanWithin only checks vertices for LineString geometries, so a line
 * segment can cross outside the service area without being detected if both endpoints
 * are inside. We supplement with booleanIntersects — checking whether the drawn line
 * geometrically crosses the service area boundary at any point.
 *
 * @param {import('ol/geom/Geometry').default} drawnGeometry - The OL geometry of the drawn/edited feature.
 * @param {import('ol/geom/MultiPolygon').default} serviceAreaGeometry - The merged service area geometry.
 * @returns {boolean} True if the drawn geometry is fully within the service area, false otherwise.
 */
export const isGeometryWithinServiceArea = (drawnGeometry, serviceAreaGeometry) => {
    if (!drawnGeometry || !serviceAreaGeometry) {
        return true; // No service area = no restriction
    }

    try {
        const drawnGeoJson = olGeometryToTurf(drawnGeometry);
        const serviceAreaGeoJson = sanitizeTurfGeometry(serviceAreaGeometry);

        if (!drawnGeoJson) {
            return true;
        }

        if (!serviceAreaGeoJson) {
            return false;
        }

        // booleanWithin expects Turf Feature objects.
        // The single point AOI from linear search points is technically a linestring with one coordinate
        // booleanWithin rejects degenerate lines, so promote it to a Point geometry
        const effectiveGeometry =
            drawnGeoJson.type === GeometryType.LINE_STRING && drawnGeoJson.coordinates?.length === 1
                ? { type: GeometryType.POINT, coordinates: drawnGeoJson.coordinates[0] }
                : drawnGeoJson;

        const drawnFeature = { type: 'Feature', geometry: effectiveGeometry, properties: {} };
        const serviceAreaFeature = { type: 'Feature', geometry: serviceAreaGeoJson, properties: {} };

        // Check all vertices are within the service area
        const withinResult = booleanWithin(drawnFeature, serviceAreaFeature);
        if (!withinResult) {
            return false;
        }

        // booleanWithin only checks vertices for LineStrings and Polygons.
        // A segment between inside vertices can still cross outside the boundary.
        // Use booleanIntersects against boundaries to detect crossing.
        if (drawnGeoJson.type === GeometryType.LINE_STRING && drawnGeoJson.coordinates?.length > 1) {
            const boundaryLine = polygonToLine(serviceAreaFeature);
            const intersectsResult = booleanIntersects(drawnFeature, boundaryLine);
            if (intersectsResult) {
                return false;
            }
        }

        if (drawnGeoJson.type === GeometryType.POLYGON && drawnGeoJson.coordinates?.[0]?.length > 1) {
            const drawnBoundary = polygonToLine(drawnFeature);
            const serviceAreaBoundary = polygonToLine(serviceAreaFeature);
            const intersectsResult = booleanIntersects(drawnBoundary, serviceAreaBoundary);
            if (intersectsResult) {
                return false;
            }
        }

        return true;
    } catch (e) {
        // Fail closed: if validation errors, treat as outside so we never incorrectly style valid.
        return false;
    }
};

/**
 * @param {import('ol/Feature').default} drawnFeature - The OL Feature being drawn/edited.
 * @param {import('ol/geom/MultiPolygon').default|object} serviceAreaGeometry - OL geometry or Turf GeoJSON geometry.
 * @returns {boolean} True if within, false if outside.
 */
export const isFeatureWithinServiceArea = (drawnFeature, serviceAreaGeometry) => {
    if (!drawnFeature || !serviceAreaGeometry) {
        return true;
    }

    const geometry = drawnFeature.getGeometry?.();
    if (!geometry) {
        return true;
    }

    return isGeometryWithinServiceArea(geometry, serviceAreaGeometry);
};
